import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ui } from "../config/theme";
import { Building2, MapPin, Loader2, Briefcase } from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";


const ExploreCompanies = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/companies`);
                if (!response.ok) throw new Error('Failed to fetch companies');
                const data = await response.json();
                setCompanies(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    if (loading) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className={`${ui.sectionCard} p-8 flex items-center justify-center`}>
                            <div className="flex items-center gap-2 text-gray-600">
                                <Loader2 className="w-6 h-6 animate-spin" />
                                <span>Loading companies...</span>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className={ui.page}>
                <Header />
                <main className="flex-grow">
                    <div className={ui.container}>
                        <div className={`${ui.sectionCard} p-8 text-center`}>
                            <p className="text-gray-600 mb-4">Error loading companies.</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className={`${ui.sectionCard} p-8`}>
                        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                            <h1 className="text-3xl font-semibold text-gray-900">Explore Companies</h1>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {companies.map((company) => {
                                // Handle both name and company_name fields
                                const companyName = company.name || company.company_name || 'Unknown Company';
                                
                                return (
                                    <div 
                                        key={company.id}
                                        className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                                        onClick={() => navigate(`/companies/${company.id}`)}
                                    >
                                        <div className="p-6">
                                            <div className="flex items-center gap-4 mb-4">
                                                {company.logo ? (
                                                    <img
                                                        src={`${API_BASE_URL}/api/companies/${company.id}/logo`}
                                                        alt={`${companyName} Logo`}
                                                        className="w-16 h-16 rounded-lg object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center" style={{ display: company.logo ? 'none' : 'flex' }}>
                                                    <Building2 className="w-8 h-8 text-gray-400" />
                                                </div>
                                                <div className="flex-1">
                                                    <h2 className="text-xl font-semibold text-gray-900 mb-1">{companyName}</h2>
                                                    {company.location && (
                                                        <div className="flex items-center text-gray-600 mb-1">
                                                            <MapPin className="w-4 h-4 mr-1" />
                                                            <span className="text-sm">{company.location}</span>
                                                        </div>
                                                    )}
                                                    {company.industry && (
                                                        <div className="flex items-center text-gray-600">
                                                            <Briefcase className="w-4 h-4 mr-1" />
                                                            <span className="text-sm">{company.industry}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {company.description && (
                                                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                                                    {company.description}
                                                </p>
                                            )}
                                            
                                            {/* Additional company info */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {company.employee_count && (
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                        {company.employee_count} employees
                                                    </span>
                                                )}
                                                {company.founded_year && (
                                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                        Founded {company.founded_year}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <div
                                                className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/companies/${company.id}`);
                                                }}
                                            >
                                                View Details
                                                <span className="ml-1">→</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {companies.length === 0 && (
                            <div className="text-center py-12">
                                <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
                                <p className="text-gray-600">There are no companies to display at the moment.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ExploreCompanies;